// src/utils/oss.js
import OSS from 'ali-oss';
import { LOCAL_API_URL, http } from "@/api/base";
let tenantId=0;
class OSSClient {
  constructor() {
    this.client = null;
  }

  /**
   * 获取临时凭证并初始化OSS客户端
   */
  // 在 private-oss.js 的 init 方法中强制刷新
  async init(tenant_id) {
    console.log('初始化 OSS 客户端...' + LOCAL_API_URL);
    tenantId = tenant_id;

    try {
      const response = await http.get(
        `/api/sts/sts-token/private?tenantId=${tenantId}`,
        { headers: { 'Content-Type': 'application/json' } }
      );

      console.log('获取临时凭证接口返回数据:', response);
      const stsToken = response.data.data;  // 修改这里：取 response.data.data
      console.log('获取临时凭证成功:', stsToken);

      console.log('🔑 STS Token 有效期检查:', {
        expiration: stsToken.expiration,
        currentTime: new Date().toISOString(),
        timeRemaining: new Date(stsToken.expiration) - new Date(),
        isExpired: new Date() > new Date(stsToken.expiration)
      });

      this.client = new OSS({
        region: stsToken.region,
        accessKeyId: stsToken.access_key_id,  // 注意字段名可能也需要调整
        accessKeySecret: stsToken.access_key_secret,
        stsToken: stsToken.security_token,
        bucket: stsToken.bucket,
        refreshSTSToken: async () => {
          const refreshResponse = await http.get(
            `${LOCAL_API_URL}/api/sts/sts-token`,
            { headers: { 'Content-Type': 'application/json' } }
          );
          const newStsToken = refreshResponse.data.data;  // 这里也要修改
          console.log('🔑 STS Token 刷新成功:', newStsToken);
          return {
            accessKeyId: newStsToken.access_key_id,
            accessKeySecret: newStsToken.access_key_secret,
            stsToken: newStsToken.security_token
          };
        },
        refreshSTSTokenInterval: 300000
      });

      return true;
    } catch (error) {
      console.error('初始化OSS客户端失败:', error);
      throw error;
    }
  }

  /**
   * 上传文件到 OSS - 私有桶低频访问
   */
  async uploadFile(file, folder = 'documents/', tenant_id = null) {
    console.log("uploadFile",file, folder, tenantId)
    if (!this.client) {
        tenantId = tenant_id;
      await this.init(tenantId);
    }

    try {
      // 生成唯一文件名
      const timestamp = new Date().getTime();
      const randomStr = Math.random().toString(36).substring(2, 8);
      const fileExtension = file.name.split('.').pop();

      // 如果有tenantId，使用租户隔离的路径
      let fileName;
      if (tenantId) {
        fileName = `tenants/${tenantId}/${folder}/${timestamp}_${randomStr}.${fileExtension}`;
      } else {
        fileName = `${folder}${timestamp}_${randomStr}.${fileExtension}`;
      }

      // 关键修改：添加存储类型头
      const result = await this.client.put(fileName, file, {
        headers: {
          'Content-Disposition': `attachment; filename=${encodeURIComponent(file.name)}`,
          'x-oss-storage-class': 'IA'  // 设置为低频访问存储
        }
      });

      console.log('文件上传成功，存储类型: IA', result);

      return {
        success: true,
        url: result.url,
        objectKey: result.name,
        fileName: file.name,
        size: file.size,
        storageClass: 'IA'
      };
    } catch (error) {
      console.error('OSS 上传失败:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async healthCheck() {
    if (!this.client) return false;

    try {
      // 尝试一个简单的 OSS 操作来验证客户端健康状态
      await this.client.listBuckets();
      return true;
    } catch (error) {
      console.log('❌ OSS 客户端健康检查失败:', error.message);
      return false;
    }
  }

  async uploadWithRetry(file, tenantId, folderName, maxRetries = 2) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await this.uploadPrivateDocument(file, tenantId, folderName);
      } catch (error) {
        console.log(`❌ 上传尝试 ${attempt}/${maxRetries} 失败:`, error.message);

        if (attempt < maxRetries) {
          // 重置客户端并重试
          console.log('🔄 重置 OSS 客户端并重试...');
          this.client = null;
          await new Promise(resolve => setTimeout(resolve, 1000)); // 等待1秒
        } else {
          throw error;
        }
      }
    }
  }

  /**
   * 专门用于文档备份的上传方法
   */
  async uploadPrivateDocument(file, tenant_id, folderName) {
    if (!this.client) {
        tenantId = tenant_id;
        await this.init(tenantId);
    }

    try {
      console.log('上传参数:', {
        tenantId,
        folderName,
        fileName: file.name,
        fileSize: file.size
      });

      const timestamp = new Date().getTime();
      const randomStr = Math.random().toString(36).substring(2, 8);
      const fileExtension = file.name.split('.').pop();

      const fileName = `tenants/${tenantId}/${folderName}/${timestamp}_${randomStr}.${fileExtension}`;

      console.log('最终 OSS 路径:', fileName);

      const result = await this.client.put(fileName, file, {
        headers: {
          'Content-Disposition': `attachment; filename=${encodeURIComponent(file.name)}`,
          'x-oss-storage-class': 'IA'
        }
      });

      console.log('上传成功:', result);
      return { success: true, fileKey: result.name };

    } catch (error) {
      console.error('上传失败详情:', {
        error: error.message,
        code: error.code,
        requestId: error.requestId,
        tenantId: tenantId,
        folderName: folderName
      });
      throw error;
    }
  }

  /**
   * 验证文件存储类型
   */
  async verifyStorageClass(fileKey) {
    if (!this.client) {
      await this.init(tenantId);
    }

    try {
      const result = await this.client.head(fileKey);
      const storageClass = result.res.headers['x-oss-storage-class'];
      console.log(`文件 ${fileKey} 的存储类型:`, storageClass);
      return storageClass;
    } catch (error) {
      console.error('验证存储类型失败:', error);
      return null;
    }
  }

}
export default new OSSClient();